import { useCallback, useEffect, useRef } from "react";
import type { RowData } from "@tanstack/react-table";
import { isEqual } from "@common/helpers/comparison";
import type { DataGridTable } from "../../columns/types";
import { getActiveRect, getRectRowsAndColumns } from "../../editing/cellRect";
import type { DataGridCellRange, UseDataGridOptions } from "../../types";

const toCellRange = <TData extends RowData>(
    table: DataGridTable<TData>,
): DataGridCellRange | null => {
    const rect = getActiveRect(table);
    if (!rect) {
        return null;
    }
    const { rows, columns } = getRectRowsAndColumns({ table, rect });
    const top = rows[0];
    const bottom = rows[rows.length - 1];
    const left = columns[0];
    const right = columns[columns.length - 1];
    return top && bottom && left && right
        ? {
              anchorRowId: top.id,
              anchorColumnId: left.id,
              focusRowId: bottom.id,
              focusColumnId: right.id,
              rowIds: rows.map((row) => row.id),
              columnIds: columns.map((column) => column.id),
          }
        : null;
};

/**
 * Calls `rangeSelection.onChange` when the active range changes, including a
 * move of the active cell, and when the rows or columns under a kept range
 * change (expand, filter, new data), which re-render the grid without
 * touching the selection.
 */
export const useRangeChangeCallback = <TData extends RowData>({
    table,
    optionsRef,
    hasListener,
}: {
    table: DataGridTable<TData>;
    optionsRef: { readonly current: UseDataGridOptions<TData> };
    hasListener: boolean;
}) => {
    const previousRef = useRef<DataGridCellRange | null>(null);
    const emit = useCallback(() => {
        const next = toCellRange(table);
        if (!isEqual(previousRef.current, next)) {
            previousRef.current = next;
            optionsRef.current.rangeSelection?.onChange?.(next);
        }
    }, [optionsRef, table]);

    useEffect(() => {
        if (!hasListener) {
            return;
        }
        const subscription = table.atoms.cellSelection.subscribe(emit);
        return () => {
            subscription.unsubscribe();
            previousRef.current = null;
        };
    }, [emit, hasListener, table]);

    useEffect(() => {
        if (hasListener) {
            emit();
        }
    });
};
